/**
 * usePerformanceMonitoring - Track Core Web Vitals in PostHog
 * Phase 6: Performance Monitoring
 *
 * Purpose: Monitor LCP, CLS, and INP metrics per tier for performance analysis
 * Tracks: Largest Contentful Paint, Cumulative Layout Shift, Interaction to Next Paint
 *
 * Web Vitals Targets (Chrome team recommendations):
 * - LCP (Largest Contentful Paint): <2.5s (good), 2.5-4s (needs improvement), >4s (poor)
 * - CLS (Cumulative Layout Shift): <0.1 (good), 0.1-0.25 (needs improvement), >0.25 (poor)
 * - INP (Interaction to Next Paint): <200ms (good), 200-500ms (needs improvement), >500ms (poor)
 *
 * Analytics Events:
 * - Performance_LCP
 * - Performance_CLS
 * - Performance_INP
 *
 * Implementation:
 * - Uses web-vitals v5 API (onLCP, onCLS, onINP)
 * - FID deprecated in v4+, replaced with INP (better responsiveness metric)
 * - Dynamic import for web platform only
 * - Graceful error handling
 */

import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useTier } from './useTier';
import { captureEvent } from '../lib/analytics';

/**
 * Polyfill Array.prototype.at() for older browsers (Safari < 15.4, iOS Safari < 15.4, Chrome < 92)
 * web-vitals@5.1.0 uses this ES2022 method internally, causing TypeError in legacy browsers.
 * Sentry Issue: DRAMA-WEB-1J
 *
 * CRITICAL: This MUST run at module scope (not in useEffect) because:
 * 1. useEffect runs AFTER React hydration
 * 2. web-vitals PerformanceObserver callbacks can fire before hydration completes
 * 3. The error occurs when web-vitals calls .at(-1) on layout-shift entries
 */
if (typeof Array.prototype.at !== 'function') {
  Array.prototype.at = function (index: number) {
    const len = this.length;
    const relativeIndex = index < 0 ? len + index : index;
    if (relativeIndex < 0 || relativeIndex >= len) return undefined;
    return this[relativeIndex];
  };
}

/**
 * Track Core Web Vitals and send to PostHog
 * Only runs on web platform
 *
 * Performance Fix (2025-12-09): Uses useRef pattern to prevent duplicate
 * listener registration on tier changes. Listeners initialize once per page
 * load, tier value captured at event time via ref.
 *
 * Compatibility Fix (2026-01-18): Moved polyfill to module scope to ensure it runs
 * before any web-vitals code executes. Sentry Issue: DRAMA-WEB-1J
 */
export function usePerformanceMonitoring() {
  const { tierLevel } = useTier();
  const tierLevelRef = useRef(tierLevel);

  // Update ref when tier changes (no effect re-run)
  useEffect(() => {
    tierLevelRef.current = tierLevel;
  }, [tierLevel]);

  useEffect(() => {
    // Only run on web platform
    if (Platform.OS !== 'web') {
      console.log('[Performance] Skipping monitoring - not running on web');
      return;
    }

    // Check if web-vitals is available
    if (typeof window === 'undefined') {
      console.log('[Performance] Skipping monitoring - window not available');
      return;
    }

    console.log('[Performance] Starting Core Web Vitals monitoring');

    // Dynamically import web-vitals (only available on web platform)
    // Note: web-vitals v4+ replaced FID with INP (Interaction to Next Paint)
    // v5 API uses onLCP, onCLS, onINP (FID is deprecated)
    import('web-vitals')
      .then(({ onCLS, onINP, onLCP }) => {
        // Track Largest Contentful Paint (LCP)
        // Target: <2.5s (good), 2.5-4s (needs improvement), >4s (poor)
        onLCP((metric) => {
          console.log('[Performance] LCP:', {
            value: Math.round(metric.value),
            rating: metric.rating,
            tier: tierLevelRef.current,
          });

          captureEvent('Performance_LCP', {
            tier: `layer${tierLevelRef.current}`,
            value_ms: Math.round(metric.value),
            rating: metric.rating,
            target_lcp_ms: 2500,
            navigation_type: metric.navigationType,
          });
        });

        // Track Cumulative Layout Shift (CLS)
        // Target: <0.1 (good), 0.1-0.25 (needs improvement), >0.25 (poor)
        onCLS((metric) => {
          console.log('[Performance] CLS:', {
            value: metric.value.toFixed(3),
            rating: metric.rating,
            tier: tierLevelRef.current,
          });

          captureEvent('Performance_CLS', {
            tier: `layer${tierLevelRef.current}`,
            value: parseFloat(metric.value.toFixed(3)),
            rating: metric.rating,
            target_cls: 0.1,
          });
        });

        // Track Interaction to Next Paint (INP) - replaces FID in v4+
        // Target: <200ms (good), 200-500ms (needs improvement), >500ms (poor)
        onINP((metric) => {
          console.log('[Performance] INP:', {
            value: Math.round(metric.value),
            rating: metric.rating,
            tier: tierLevelRef.current,
          });

          captureEvent('Performance_INP', {
            tier: `layer${tierLevelRef.current}`,
            value_ms: Math.round(metric.value),
            rating: metric.rating,
            target_inp_ms: 200,
          });
        });
      })
      .catch((error) => {
        console.error('[Performance] Failed to load web-vitals:', error);
      });
  }, []); // ← CRITICAL FIX: Empty dependency array prevents re-initialization
}
